import type { FileUIPart } from 'ai';

import {
  ATTACHMENT_MAX_IMAGE_SIZE_BYTES,
  isSvgFile,
  rasterizeSvgToPngDataUrl,
  readFileAsDataUrl,
  resolveMediaType,
} from '@bizzdesign/shared/utils';

/**
 * Read a picked file into an AI-SDK `file` UI part (data URL + metadata).
 *
 * @returns A promise resolving to a {@link FileUIPart} that can be passed to
 * `chat.sendMessage({ files })` and rendered as an attachment chip.
 * @remarks SVGs are rasterized to PNG here (the vision model cannot read
 * `image/svg+xml`). When the browser reports no `type`, the media type is
 * derived from the file extension — `application/octet-stream` reaches the model
 * provider as an unsupported document format and fails the request.
 */
export async function fileToFileUIPart(file: File): Promise<FileUIPart> {
  if (isSvgFile(file)) {
    return {
      type: 'file',
      filename: withPngExtension(file.name),
      mediaType: 'image/png',
      url: await rasterizeSvgToPngDataUrl(
        file,
        ATTACHMENT_MAX_IMAGE_SIZE_BYTES,
      ),
    };
  }
  const url = await readFileAsDataUrl(file);
  return {
    type: 'file',
    filename: file.name,
    mediaType: resolveMediaType(file),
    url,
  };
}

function withPngExtension(filename: string): string {
  return `${filename.replace(/\.svg$/i, '')}.png`;
}
